import { fetchBaseQuery, retry } from "@reduxjs/toolkit/query";
import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError
} from "@reduxjs/toolkit/query";
import { appLoaderActions } from "../store/slices/appLoaderSlice";
import { ClientError, request } from "graphql-request";
import messageCode from "../assets/config/message-code-fe.json";
import { v4 as uuidv4 } from "uuid";
import { KeysOf } from "../store/slices/query-args/searchCatalog";
import Urls from "./urls";

const getErrorStatus = (error: unknown) => {
  if (error instanceof ClientError) {
    return error.response.status ?? 500;
  }

  if (
    typeof error === "object" &&
    error !== null &&
    "status" in error &&
    typeof (error as { status?: unknown }).status === "number"
  ) {
    return (error as { status: number }).status;
  }

  return 500;
};

const toSerializableErrorData = (
  value: unknown,
  seen = new WeakSet<object>()
): unknown => {
  if (
    value === null ||
    value === undefined ||
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return value;
  }

  if (typeof value === "bigint") {
    return value.toString();
  }

  if (Array.isArray(value)) {
    return value.map((item) => toSerializableErrorData(item, seen));
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  if (value instanceof Error) {
    const serializedError: Record<string, unknown> = {
      name: value.name,
      message: value.message
    };

    if (value.stack) {
      serializedError.stack = value.stack;
    }

    Object.entries(value).forEach(([key, nestedValue]) => {
      serializedError[key] = toSerializableErrorData(nestedValue, seen);
    });

    return serializedError;
  }

  if (typeof value === "object") {
    if (seen.has(value)) {
      return "[Circular]";
    }

    seen.add(value);

    const serializedObject = Object.entries(value).reduce<
      Record<string, unknown>
    >((accumulator, [key, nestedValue]) => {
      accumulator[key] = toSerializableErrorData(nestedValue, seen);
      return accumulator;
    }, {});

    seen.delete(value);

    return serializedObject;
  }

  return String(value);
};

type ExtraOptions = {
  service?: KeysOf<typeof Urls>;
};

interface CustomFetchArgs extends FetchArgs {
  customuuid?: string;
  url: `/${string | "graphql"}`;
}

type CustomBaseQueryFn = BaseQueryFn<
  CustomFetchArgs,
  unknown,
  FetchBaseQueryError,
  ExtraOptions
>;

const baseQuery = retry(
  fetchBaseQuery({
    baseUrl: `${import.meta.env.VITE_TMS_URL}`,
    prepareHeaders(headers, { getState }: any) {
      const uid = getState().UserInfoSlice.profile.id;
      const org = getState().UserInfoSlice.org;
      const token = getState().UserInfoSlice.token;
      headers.set("x-muse-locale", "en_IN");
      if (!headers.has("x-muse-uuid") && uid != undefined) {
        headers.set("x-muse-uuid", uid);
      }
      headers.set("x-muse-version", "v1");
      headers.set("x-muse-org", org);
      headers.set("x-muse-zone", "Asia/Kolkata");
      headers.set("Accept", "*/*");
      headers.set("Authorization", `Bearer ${token}`);
      headers.set("x-muse-request-id", `RQ${uuidv4()}`);
      headers.set("x-muse-caller", "glx");
      return headers;
    }
  }),
  { maxRetries: 0 }
);

const customBaseQuery: CustomBaseQueryFn = async (args, api, extraOptions) => {
  const { dispatch } = api;
  const getState: () => any = api.getState;
  dispatch(appLoaderActions.setIsLoading(true));
  const { method, body, customuuid } = args;
  try {
    const REST = Boolean(method);
    const uuid = customuuid ? customuuid : getState().UserInfoSlice.profile.id;
    const org = getState().UserInfoSlice.org;
    const token = getState().UserInfoSlice.token;
    const header = {
      "x-muse-uuid": uuid,
      "x-muse-org": org,
      "x-muse-version": "v1",
      "x-muse-zone": "Asia/Kolkata",
      "x-muse-locale": "en_IN",
      "x-muse-request-id": `RQ${uuidv4()}`,
      "x-muse-caller": "glx",
      "Content-Type": "multipart/form-data",
      Authorization: `Bearer ${token}`
    };

    const updatedArgs = { ...args };

    if (extraOptions?.service) {
      updatedArgs.url = (Urls[extraOptions.service!] + updatedArgs.url) as any;
    } else {
      if (!REST) {
        updatedArgs.url = (Urls["TMS"] + updatedArgs.url) as any;
      }
    }

    const result = REST
      ? await baseQuery(
          {
            ...updatedArgs,
            headers: {
              ...updatedArgs.headers,
              ...(uuid != undefined && { "x-muse-uuid": uuid })
            }
          },
          api,
          {
            maxRetries: 0
          }
        )
      : ((await request(
          updatedArgs.url,
          body,
          args?.params?.variables,
          header
        )) as any);
    dispatch(appLoaderActions.setIsLoading(false));
    return REST ? result : { data: result };
  } catch (error) {
    dispatch(appLoaderActions.setIsLoading(false));
    return {
      error: {
        status: getErrorStatus(error),
        data: toSerializableErrorData(error)
      }
    };
  }
};

export default customBaseQuery;

type RemoveErrorSuffix<T extends string> = T extends `${infer Prefix}_error`
  ? Prefix
  : T;
type AddErrorSuffix<T extends string> = `${T}_error`;

export interface IBaseQueryResponse<T> {
  timestamp: string;
  data: T;
  error: RemoveErrorSuffix<AddErrorSuffix<keyof typeof messageCode>>[];
  version: string;
}

export type IBaseMutationResponse = IBaseQueryResponse<{
  id: string;
  message: RemoveErrorSuffix<AddErrorSuffix<keyof typeof messageCode>>;
}>;
